import { READING_LINE, readingLine } from "@/lib/memoFocusUnits";

/** A focused unit's placement in the presentation scroll container. */
export interface UnitPlacement {
  /** The unit's top, relative to the container's top (negative = above the fold). */
  elTop: number;
  elHeight: number;
  viewportH: number;
  /** The container's current scroll offset — the probe is clamped by it. */
  scrollTop: number;
}

const MARGIN = 24;

/**
 * The INVERSE of the reading line's position in document space.
 *
 * The probe is `p(s) = s + min(H/3, s)` — `2s` while the clamp bites, `s + H/3`
 * afterwards. Strictly increasing, hence invertible: `p⁻¹` answers "what scroll
 * offset puts the reading line on this document position?". That is how the step
 * parks a unit so the GRAB will hand it back — the two share one probe, and solving
 * in document space is what makes the invariant hold near the top of the memo too,
 * where the clamp makes the viewport-space line a moving target.
 */
const probeDocInverse = (probe: number, viewportH: number) => {
  const cap = viewportH * READING_LINE;
  return probe <= 2 * cap ? probe / 2 : probe - cap;
};

/**
 * How far to scroll so the STEPPED-TO unit reads well — 0 to leave it alone.
 *
 * 🔴 THE INVARIANT: a unit the cursor has stepped to must OWN THE READING LINE —
 * the same probe `pickNearestUnit` grabs with. Otherwise the two disagree, and they
 * did: this function used to CENTRE a unit that fits, and for any unit shorter than
 * a third of the screen `(H - h) / 2 > H / 3` **always**. So the step parked a short
 * Zwischenabschnitt with its top BELOW the reading line — where the line lands in the
 * PREVIOUS unit — and the next grab handed the cursor backwards. On a run of short
 * sections it walked back two units, and `Enter` then filed the entry into the wrong
 * Zwischenabschnitt on the shared screen. A 1px trackpad nudge is enough to trigger
 * it, because any wheel event releases the lock.
 *
 * That is the same defect the grab rule itself had ("a unit shorter than H/3 is
 * unreachable") — fixed in the pick, and immediately reintroduced by the positioner.
 * The two now share one probe, and it is computed in DOCUMENT space so the invariant
 * also holds near the top of the memo, where the clamp makes the viewport-space line
 * a moving target.
 *
 * 🔴 It runs ONLY on a STEP, never on a GRAB. A grab locks the unit the reader is
 * already inside, at the scroll position they chose; re-positioning it drags the
 * shared Teams screen backwards — up to 2400px, mid-sentence. See MemoPage.
 */
export function unitScrollDelta({ elTop, elHeight, viewportH, scrollTop }: UnitPlacement): number {
  const line = readingLine(0, viewportH, scrollTop);
  const fits = elHeight <= viewportH - MARGIN * 2;

  // Already owns the reading line, and (if it fits) is wholly on screen: leave it.
  // The dead-zone matters — stepping between two units that share the screen must
  // not fire a smooth re-scroll on every press.
  const ownsLine = elTop <= line && elTop + elHeight > line;
  const wholly = !fits || (elTop >= MARGIN && elTop + elHeight <= viewportH - MARGIN);
  if (ownsLine && wholly) return 0;

  // Put the probe a little way INTO the unit — half its height, capped so a tall
  // section still starts near the top of the screen rather than being entered at its
  // middle.
  const docTop = scrollTop + elTop;
  const target = docTop + Math.min(elHeight / 2, (viewportH * READING_LINE) / 2);
  let desired = probeDocInverse(target, viewportH);

  // A unit that FITS should also be wholly visible: never park it so low that its
  // tail is cut off. (This can only pull it UP, so it cannot break `ownsLine`: the
  // unit's top only moves further above the line.)
  if (fits) desired = Math.max(desired, docTop - (viewportH - MARGIN - elHeight));

  return Math.max(0, desired) - scrollTop;
}
